/*
	Site menu drawer navigation.

	Moves between the three levels rendered by sections/drawer-navigation.liquid. Each
	level is a sibling panel; navigating swaps which one is in the flow and animates the
	wrapper height so the shared footer follows the new level smoothly.

	Clicks on [data-menu-open] stay in the drawer. Leaf links have no data-menu-open, so
	they keep the URL set on the item in Navigation.
*/

class MenuDrawerNav extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.duration = 260;
    this.stack = this.querySelector('[data-menu-panels]');
    this.panels = Array.from(this.querySelectorAll('[data-menu-panel]'));
    this.rootPanel = this.panels.find((panel) => panel.dataset.menuPanel === 'root');

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-menu-open]');
      if (opener && this.contains(opener)) {
        const key = opener.dataset.menuOpen;
        const next = this.panels.find((panel) => panel.dataset.menuPanel === key);
        if (next) {
          event.preventDefault();
          event.stopPropagation();
          this._goTo(key, 'forward', opener);
        }
        return;
      }

      const back = event.target.closest('[data-menu-back]');
      if (back && this.contains(back)) {
        event.preventDefault();
        event.stopPropagation();
        this._goTo(back.dataset.menuBack, 'backward');
      }
    });

    // The drawer has no close event, so watch the class it toggles and rewind.
    const drawer = this.closest('sidebar-drawer');
    if (drawer) {
      new MutationObserver(() => {
        if (!drawer.classList.contains('kt-sidebar--opened')) {
          this.reset();
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  reset() {
    clearTimeout(this._heightTimer);
    this.panels.forEach((panel) => {
      panel.hidden = panel !== this.rootPanel;
      panel.classList.remove(
        'kt-menu-drawer__panel--enter-forward',
        'kt-menu-drawer__panel--enter-backward',
      );
    });
    this.querySelectorAll('[data-menu-open]').forEach((elm) =>
      elm.setAttribute('aria-expanded', 'false'),
    );
    if (this.stack) this.stack.style.height = '';
  }

  _goTo(key, direction, opener = null) {
    const next = this.panels.find((panel) => panel.dataset.menuPanel === key);
    const current = this.panels.find((panel) => !panel.hidden);
    if (!next || !this.stack || next === current) return;

    const fromHeight = this.stack.offsetHeight;

    this.panels.forEach((panel) => {
      panel.hidden = panel !== next;
    });

    const toHeight = this.stack.offsetHeight;

    this.querySelectorAll('[data-menu-open]').forEach((elm) =>
      elm.setAttribute('aria-expanded', 'false'),
    );
    if (opener) opener.setAttribute('aria-expanded', 'true');

    const enterClass =
      direction === 'backward'
        ? 'kt-menu-drawer__panel--enter-backward'
        : 'kt-menu-drawer__panel--enter-forward';

    next.classList.remove(
      'kt-menu-drawer__panel--enter-forward',
      'kt-menu-drawer__panel--enter-backward',
    );
    void next.offsetWidth;
    next.classList.add(enterClass);

    clearTimeout(this._heightTimer);
    this.stack.style.height = `${fromHeight}px`;
    requestAnimationFrame(() => {
      this.stack.style.height = `${toHeight}px`;
    });
    this._heightTimer = setTimeout(() => {
      this.stack.style.height = '';
    }, this.duration);

    this.closest('sidebar-drawer')?.scrollTo({ top: 0 });
    next.focus({ preventScroll: true });
  }
}

if (!customElements.get('menu-drawer-nav')) {
  customElements.define('menu-drawer-nav', MenuDrawerNav);
}
